import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from 'app/_utils/auth/auth.service';
import { VehiculoLocalizacion } from 'app/_interfaces/vehiculo-localizacion';

import * as mockResponse from '../../../assets/response_api_loc_inst.json';

@Injectable({
  providedIn: 'root'
})
export class RealtimeDataService {

  constructor(private readonly http: HttpClient) {}

  private readonly MAX_IDS_DIRECCION = 20;

  private get useMock(): boolean {
    return environment.useMockLocalizacion;
  }

  private get baseUrl(): string {
    return `${environment.endPoint}/ClientSite/api/GetLocalizacion`;
  }

  private buildHeaders(token: string): HttpHeaders {
    return new HttpHeaders({ 'Authorization': `Bearer ${token}` });
  }

  private getMockData(): { data: VehiculoLocalizacion[] } {
    return (mockResponse as any).default || mockResponse as any;
  }

  /**
   * Obtiene todos los vehículos del cliente con su última ubicación, IOs y ADCs.
   */
  getLocalizacion(idCliente: number): Observable<{ data: VehiculoLocalizacion[] }> {
    if (this.useMock) {
      return of(this.getMockData());
    }
    return from(AuthService.getAt()).pipe(
      switchMap((token: string) => {
        const params = new HttpParams()
          .set('idClient', String(idCliente))
          .set('sensorInfo', 'true')
          .set('adcInfo', 'true')
          .set('ioInfo', 'true')
          .set('modelo','true')
          .set('batteryPercent','true');
        return this.http.post<{ data: VehiculoLocalizacion[] }>(
          this.baseUrl,
          null,
          { headers: this.buildHeaders(token), params, withCredentials: true }
        );
      })
    );
  }

  /**
   * Obtiene la última localización de un vehículo específico.
   * Filtra por vehicleId en el body del POST.
   * El imei se usa solo para identificar el registro correcto en la respuesta (filtro frontend).
   * Siempre solicita ioInfo=true y direccion=true.
   */
  getLocalizacionByImei(imei: number | string, vehicleId?: number): Observable<VehiculoLocalizacion> {
    if (this.useMock) {
      const mock = this.getMockData();
      const found = mock.data.find(v => String(v.imei) === String(imei));
      return of(found !== undefined ? found : null);
    }
    const idCliente = Number(localStorage.getItem('idcliente'));
    return from(AuthService.getAt()).pipe(
      switchMap((token: string) => {
        const params = new HttpParams()
          .set('idClient', String(idCliente))
          .set('ioInfo', 'true')
          .set('sensorInfo', 'true')
          .set('adcInfo', 'true')
          .set('direccion', 'true')
          .set('modelo','true')
          .set('batteryPercent','true');
        const body = vehicleId ? { vehicleIds: [vehicleId] } : null;
        return this.http.post<{ data: VehiculoLocalizacion[] }>(
          this.baseUrl,
          body,
          { headers: this.buildHeaders(token), params, withCredentials: true }
        ).pipe(
          map(res => { const f = res.data.find(v => String(v.imei) === String(imei)); return f !== undefined ? f : null; })
        );
      })
    );
  }

  /**
   * Obtiene la dirección de los vehículos indicados por vehicleId.
   * En mock filtra el JSON local (sin campo direccion, solo para pruebas de flujo).
   */
  getLocalizacionConDirecciones(idCliente: number, vehicleIds: number[]): Observable<VehiculoLocalizacion[]> {
    if (this.useMock) {
      const mock = this.getMockData();
      return of(mock.data.filter(v => vehicleIds.includes(v.vehicleId)));
    }
    const chunks = this.chunkArray(vehicleIds, this.MAX_IDS_DIRECCION);
    return from(AuthService.getAt()).pipe(
      switchMap((token: string) => {
        const headers = this.buildHeaders(token);
        const params = new HttpParams()
          .set('idClient', String(idCliente))
          .set('direccion', 'true');
        const requests = chunks.map(chunk =>
          this.http.post<{ data: VehiculoLocalizacion[] }>(
            this.baseUrl,
            { vehicleIds: chunk },
            { headers, params, withCredentials: true }
          ).pipe(map(res => res.data !== undefined && res.data !== null ? res.data : []))
        );
        return forkJoin(requests).pipe(
          map(results => ([] as VehiculoLocalizacion[]).concat(...results))
        );
      })
    );
  }

  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  /** @deprecated Usar getLocalizacion() */
  getVehicleByIdVehicle(idCliente: number, _ioInfo?: boolean): Observable<{ data: VehiculoLocalizacion[] }> {
    return this.getLocalizacion(idCliente);
  }

  /** @deprecated Usar getLocalizacion() */
  getVehiclesList(idCliente: number): Observable<{ data: VehiculoLocalizacion[] }> {
    return this.getLocalizacion(idCliente);
  }
}
